/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { Component } from 'react';
import { TextField } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import { ActionMenuItemProps } from './ActionMenu';
import { matchesEmailPattern } from '../../utils/Util';
import DialogActionView from './DialogAction.view';

const emailValidationMessage = 'Verwende bitte eine gültige E-Mail-Adresse wie anna@beispiel.de';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-unused-vars
const styles = (theme: Theme) => {
	return createStyles({
		emailField: {
			overflowY: 'visible',
			height: 100
		}
	});
};

interface Props extends ActionMenuItemProps, WithStyles<typeof styles> {
	currentEmail: string;
	handleSave: (newEmail: string) => void;
}

interface State {
	newEmail: string;
	newEmailValid: boolean;
}

const initialState = (props: Props): State => ({
	newEmail: props.currentEmail,
	newEmailValid: true
});

class ChangeEmailActionView extends Component<Props, State> {
	constructor(props: Props) {
		super(props);

		this.state = initialState(props);
		this.handleOpenDialog = this.handleOpenDialog.bind(this);
		this.handleChange = this.handleChange.bind(this);
		this.handleSave = this.handleSave.bind(this);
		this.isSaveDisabled = this.isSaveDisabled.bind(this);
	}

	handleOpenDialog(): void {
		this.setState({ ...initialState(this.props) });
	}

	handleSave(): void {
		this.props.handleSave(this.state.newEmail);
	}

	handleChange(event: React.ChangeEvent<{ value: string }>): void {
		const newEmail = event.target.value;

		const newEmailValid = matchesEmailPattern(newEmail);

		this.setState({ newEmail, newEmailValid });
	}

	isSaveDisabled(): boolean {
		return !this.state.newEmailValid || this.props.currentEmail === this.state.newEmail;
	}

	render(): JSX.Element {
		const { renderMenuItem, classes } = this.props;
		const { newEmail, newEmailValid } = this.state;
		return (
			<DialogActionView
				itemProps={{
					renderMenuItem,
					icon: <AlternateEmailIcon color="primary" />,
					text: 'E-Mail bearbeiten'
				}}
				dialogProps={{
					titleText: 'E-Mail-Adresse bearbeiten',
					saveButtonText: 'Speichern',
					handleOpen: this.handleOpenDialog,
					handleSave: this.handleSave,
					handleIsSaveDisabled: this.isSaveDisabled
				}}
			>
				<TextField
					variant="standard"
					id="email"
					name="email"
					label="E-Mail-Adresse"
					type="email"
					value={newEmail}
					className={classes.emailField}
					onChange={this.handleChange}
					InputLabelProps={{
						shrink: true
					}}
					fullWidth
					autoFocus
					error={!newEmailValid}
					helperText={!newEmailValid ? emailValidationMessage : ''}
				/>
			</DialogActionView>
		);
	}
}

export default withStyles(styles)(ChangeEmailActionView);
